import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, Layers, ShieldCheck, LogOut, Microscope, Scale, Brain, Flame, Settings, Presentation, ScanLine, Network, Menu, X, FileCheck2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang, LANGS } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { canUsePWA } from "@/lib/pwa";
import NotificationBell from "@/components/NotificationBell";
import InstallPrompt from "@/components/InstallPrompt";
import InviteDialog from "@/components/InviteDialog";
import { IdleLogout } from "@/components/IdleLogout";

const NAV = [
  { to: "/", key: "nav_dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/mes-rapports", label: "Mes rapports signés", icon: FileCheck2, roles: ["clinicien"] },
  { to: "/patients", key: "nav_patients", icon: Users, deny: ["admin_technique", "clinicien"] },
  { to: "/demandes", key: "nav_demandes", icon: FileText, deny: ["admin_technique"] },
  { to: "/parties", key: "nav_parties", icon: Layers, deny: ["admin_technique"] },
  { to: "/intervenants", key: "nav_intervenants", icon: Network, roles: "all" },
  { to: "/wsi", key: "nav_wsi", icon: Microscope, deny: ["admin_technique"] },
  { to: "/paillasse", key: "nav_paillasse", icon: ScanLine, deny: ["admin_technique", "clinicien", "secretaire", "responsable_qualite", "directeur_medical", "expert_externe"] },
  { to: "/analyses", key: "nav_analyses", icon: Brain, deny: ["admin_technique", "secretaire", "clinicien"] },
  { to: "/admin", key: "nav_admin", icon: ShieldCheck,
    roles: ["admin_technique", "chef_service", "responsable_qualite", "directeur_medical"] },
  { to: "/conformite", key: "nav_conformite", icon: Scale, roles: "all" },
];

function allowed(item, role) {
  if (item.roles === "all") return true;
  if (item.deny) return !item.deny.includes(role);
  if (Array.isArray(item.roles)) return item.roles.includes(role);
  return true;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { theme, setTheme, themes } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [iaCount, setIaCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (user?.theme && user.theme !== theme) setTheme(user.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.theme]);

  const pickTheme = (key) => {
    setTheme(key);
    api.put("/auth/me/theme", { theme: key }).catch(() => {});
  };

  useEffect(() => {
    const roles = ["chef_service", "pathologiste", "pathologiste_distant", "directeur_medical", "expert_externe"];
    if (!roles.includes(user?.role)) return undefined;
    let stop = false;
    const load = () => api.get("/analyses/haut-risque")
      .then(({ data }) => { if (!stop) setIaCount(data.count || 0); }).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    const onNotify = () => load();
    window.addEventListener("pi-notify", onNotify);
    return () => { stop = true; clearInterval(timer); window.removeEventListener("pi-notify", onNotify); };
  }, [user?.role, location.pathname]);

  return (
    <div className="flex min-h-screen">
      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop"
             className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" />
      )}
      {/* Sidebar */}
      <aside className={`w-64 flex-shrink-0 text-slate-100 fixed lg:sticky inset-y-0 left-0 top-0 h-screen z-40 flex flex-col transform transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
             style={{ background: "#0F2942" }} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-700/60 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "#0284C7" }}>
            <Microscope className="h-5 w-5" />
          </div>
          <div>
            <div className="font-head font-bold leading-none">Path-Intelligent</div>
            <div className="text-[10px] text-sky-200 mt-1">LIS Pathologie · Québec</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} data-testid="sidebar-close"
                  className="ml-auto lg:hidden p-1.5 rounded-md text-slate-300 hover:bg-slate-700/50">
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.filter((i) => allowed(i, user?.role)).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.to === "/"}
                data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive ? "bg-sky-600 text-white font-medium" : "text-slate-300 hover:bg-slate-700/50"
                  }`}>
                <Icon className="h-4 w-4" /> {item.label || t(item.key)}
                {item.to === "/demandes" && iaCount > 0 && (
                  <span data-testid="nav-ia-highrisk-badge" title="Cas IA à haut risque non consultés"
                        className="ml-auto text-[10px] font-bold bg-red-600 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {iaCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <InviteDialog />
        <a href="/presentation.html" target="_blank" rel="noopener" data-testid="nav-presentation"
           className="mx-3 mb-2 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-300 hover:bg-slate-700/50 transition-colors no-underline">
          <Presentation className="h-4 w-4" /> Présentation
        </a>
        <Link to="/fondation" title="Fondation Dr Ahlem AOUACHRI-MAKHLOUF" className="px-5 py-3 border-t border-slate-700/60 flex items-center gap-2.5 no-underline hover:bg-slate-800/40 transition-colors" data-testid="memorial-sidebar">
          <img src="/ahlem-portrait.jpg" alt="Dr Ahlem" className="h-7 w-7 rounded-full object-cover ring-1 ring-amber-300/60 shrink-0" />
          <Flame className="h-3.5 w-3.5 memorial-icon" />
          <span className="memorial-shine text-[11px] tracking-wide">Projet mémoriel · Fondation Dr Ahlem →</span>
        </Link>
      </aside>

      {/* Contenu */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle"
                    aria-label="Ouvrir le menu"
                    className="lg:hidden p-2 -ml-2 rounded-md text-slate-600 hover:bg-slate-100">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-teal-600" />
              <span className="hidden sm:inline">{t("top_secure")}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-1 mr-1" data-testid="theme-picker" title="Couleur du thème">
            {themes.map((th) => (
              <button key={th.key} onClick={() => pickTheme(th.key)} title={th.label} data-testid={`theme-${th.key}`}
                aria-label={`Thème ${th.label}`}
                className={`h-4 w-4 rounded-full transition-transform hover:scale-110 ${theme === th.key ? "ring-2 ring-offset-1 ring-slate-500" : ""}`}
                style={{ background: th.hex }} />
            ))}
          </div>
          <button onClick={() => setLang(LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length])} data-testid="lang-toggle"
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-full px-2.5 py-1 hover:bg-slate-100">
              {lang.toUpperCase()}
            </button>
            {canUsePWA(user?.role) && <NotificationBell />}
            <Link to="/profile" data-testid="nav-profile"
                  className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-sky-700">
              <Settings className="h-4 w-4" />
            </Link>
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium text-slate-800" data-testid="topbar-user-name">{user?.nom}</div>
              <div className="text-[11px] text-sky-700">{t(`role_${user?.role}`)}</div>
            </div>
            <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
              {(user?.nom || "?").slice(0, 2).toUpperCase()}
            </div>
            <button onClick={() => { logout(); navigate("/login"); }}
                    data-testid="logout-button"
                    className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-red-600">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1920px] w-full mx-auto">{children}</main>
      </div>
      <InstallPrompt />
      <IdleLogout />
    </div>
  );
}
